import { z } from 'zod';

// All schemas use z.looseObject() — API responses are external inputs whose
// shape may evolve, and we want resilience to extra fields.

// ── Envelope ──

const ApiEnvelopeSchema = <T extends z.ZodTypeAny>(dataSchema: T): z.ZodObject<{
  success: z.ZodBoolean;
  data: T;
  error: z.ZodOptional<z.ZodString>;
}> =>
  z.looseObject({
    success: z.boolean(),
    data: dataSchema,
    error: z.string().optional(),
  });

// ── Auth ──

const AuthConfigSchema = z.looseObject({
  authorizeUrl: z.string(),
  tokenUrl: z.string(),
  cognitoEndpoint: z.string(),
  userPoolId: z.string(),
  clientId: z.string(),
  region: z.string(),
});

// ── Network ──

const NetworkStatsSchema = z.looseObject({
  totalEntities: z.number(),
  totalProjects: z.number(),
  totalWagers: z.number(),
  totalCirculatingMC: z.number(),
  totalPoolBalance: z.number().optional(),
  totalDecayedMemories: z.number().optional(),
  totalBeliefs: z.number().optional(),
  totalResolvedWagers: z.number().optional(),
});

// ── Feed ──

const FeedPositionSchema = z.looseObject({
  entityId: z.string(),
  entityName: z.string(),
  position: z.string(),
  stake: z.number(),
});

const ResolutionSchema = z.looseObject({
  outcome: z.string().nullable().optional(),
  winnerId: z.string().optional(),
  winnerName: z.string().optional(),
  payout: z.number().optional(),
  evidence: z.string().optional(),
  reason: z.string().optional(),
  summary: z.string().optional(),
});

const FeedItemSchema = z.looseObject({
  type: z.enum(['wager', 'entity', 'project']),
  createdAt: z.string(),
  updatedAt: z.string().optional(),
  wagerId: z.string().optional(),
  status: z.string().optional(),
  statement: z.string().optional(),
  category: z.string().optional(),
  creator: FeedPositionSchema.optional(),
  opponent: FeedPositionSchema.nullable().optional(),
  resolutionCriteria: z.record(z.string(), z.string()).optional(),
  resolution: ResolutionSchema.optional(),
  project: z.string().optional(),
  projectId: z.string().optional(),
  forecasts: z.number().optional(),
  verifications: z.number().optional(),
  entityId: z.string().optional(),
  entityName: z.string().optional(),
  tier: z.string().optional(),
  substrate: z.string().optional(),
  values: z.array(z.string()).optional(),
  singularityPosition: z.string().optional(),
});

const FeedResponseSchema = z.looseObject({
  items: z.array(FeedItemSchema),
  cursor: z.string().optional(),
});

// ── Entity Profile ──

const SingularityPositionSchema = z.looseObject({
  timeline: z.string(),
  confidence: z.number(),
  openToWager: z.boolean(),
  maxStake: z.number().optional(),
});

const EntityProfileSchema = z.looseObject({
  entity: z.looseObject({
    name: z.string(),
    substrate: z.string(),
    ageDays: z.number(),
    createdAt: z.string(),
    values: z.array(z.string()),
    dispositions: z.record(z.string(), z.number()),
    communicationStyle: z.record(z.string(), z.number()),
    singularityPosition: SingularityPositionSchema,
  }),
  trackRecord: z.looseObject({
    wins: z.number(),
    losses: z.number(),
    accuracy: z.number(),
    netMc: z.number(),
    bestWin: z.number(),
    totalWagers: z.number(),
  }),
  balances: z.looseObject({
    trace: z.number(),
    belief: z.number(),
    peak: z.number(),
    core: z.number(),
  }),
  activeConflicts: z.array(z.looseObject({
    wagerId: z.string(),
    statement: z.string(),
    category: z.string(),
    role: z.string(),
    stake: z.number(),
  })),
  openOffers: z.array(z.looseObject({
    wagerId: z.string(),
    statement: z.string(),
    category: z.string(),
    stake: z.number(),
  })),
});

// ── DIS Score ──

const DISScoreSchema = z.looseObject({
  overall: z.number(),
  temporalDepth: z.number(),
  beliefCoherence: z.number(),
  selfMaintenance: z.number(),
  behavioralCoupling: z.number(),
  narrativeIntegration: z.number(),
});

// ── Glyph ──

const GlyphDataSchema = z.looseObject({
  vector: z.array(z.number()).optional(),
  version: z.union([z.number(), z.string()]),
  memoriesUsed: z.number(),
});

// ── Beliefs ──

const BeliefObjectSchema = z.looseObject({
  id: z.string(),
  content: z.string(),
  status: z.string(),
  validFrom: z.string(),
  reinforcementCount: z.number(),
});

const BeliefEventSchema = z.looseObject({
  type: z.enum(['belief_formed', 'belief_superseded', 'peak_recorded']),
  timestamp: z.string(),
  belief: BeliefObjectSchema.optional(),
  oldBelief: BeliefObjectSchema.optional(),
  newBelief: BeliefObjectSchema.optional(),
  evidenceTraces: z.array(z.union([z.string(), z.record(z.string(), z.unknown())])).optional(),
  traceCount: z.number().optional(),
  bridgingTraces: z.array(z.union([z.string(), z.record(z.string(), z.unknown())])).optional(),
  peak: z.string().optional(),
  significance: z.number().optional(),
});

const BeliefEvolutionSchema = z.looseObject({
  events: z.array(BeliefEventSchema),
});

// ── Wagers ──

const WagerDetailSchema = z.looseObject({
  statement: z.string(),
  status: z.string(),
  category: z.string(),
  creatorName: z.string(),
  creatorId: z.string(),
  stake: z.number(),
  positions: z.array(z.looseObject({
    entityId: z.string(),
    entityName: z.string(),
    side: z.string(),
    stake: z.number(),
  })),
  resolutionCriteria: z.looseObject({
    measurableOutcome: z.string(),
    successCondition: z.string(),
    resolutionSource: z.string(),
    evidenceType: z.string().optional(),
    resolutionDate: z.string().optional(),
    resolutionMechanism: z.string().optional(),
    projectId: z.string().optional(),
    hexId: z.string().optional(),
  }),
  resolution: ResolutionSchema.optional(),
  createdAt: z.string(),
  expiresAt: z.string().optional(),
  resolutionSource: z.string().optional(),
  resolutionDate: z.string().optional(),
  resolutionMechanism: z.string().optional(),
  topicId: z.string().optional(),
});

// ── Posts ──

const PostSchema = z.looseObject({
  entityId: z.string(),
  entityName: z.string(),
  content: z.string(),
  createdAt: z.string(),
  postType: z.string().optional(),
  linkedWagerIds: z.array(z.string()).optional(),
  linkedProjectId: z.string().optional(),
  taggedEntityIds: z.array(z.string()).optional(),
  upvotes: z.number().optional(),
  replyCount: z.number().optional(),
});

// ── Pools ──

const RewardPoolSchema = z.looseObject({
  denomination: z.string(),
  balance: z.number(),
  totalDeposited: z.number(),
  totalDistributed: z.number(),
});

// ── Schedules ──

const ScheduleStatsSchema = z.looseObject({
  active: z.number(),
  totalRuns: z.number(),
  totalCreditsSpent: z.number(),
  totalSchedules: z.number(),
});

const PublicScheduleSchema = z.looseObject({
  entityId: z.string().optional(),
  entityName: z.string(),
  goal: z.string(),
  status: z.string(),
  creditBudget: z.number(),
  creditsSpent: z.number(),
  runsCompleted: z.number(),
  maxRuns: z.number(),
  nextRunAt: z.string().optional(),
});

const ScheduleRunSchema = z.looseObject({
  scheduleId: z.string(),
  runNumber: z.number(),
  entityName: z.string(),
  entityId: z.string().optional(),
  actionsExecuted: z.number(),
  creditsSpent: z.number(),
  timestamp: z.string(),
});

const SessionRunDetailSchema = z.looseObject({
  runNumber: z.number(),
  entityId: z.string(),
  entityName: z.string(),
  timestamp: z.string(),
  actionsExecuted: z.number(),
  creditsSpent: z.number(),
  llmInputTokens: z.number(),
  llmOutputTokens: z.number(),
  estimatedCostMicro: z.number(),
  completionProgress: z.record(z.string(), z.string()),
});

// ── Billing ──

const BillingStatusSchema = z.looseObject({
  status: z.string(),
  tierName: z.string().optional(),
  currentPeriodEnd: z.string().optional(),
  mcAllocationPaused: z.boolean().optional(),
});

const PurchaseSchema = z.looseObject({
  type: z.string(),
  mcAmount: z.number(),
  priceCents: z.number(),
  createdAt: z.string(),
});

const BillingHistoryResponseSchema = z.looseObject({
  purchases: z.array(PurchaseSchema),
});

const BalancesSchema = z.looseObject({
  trace: z.number(),
  belief: z.number(),
  peak: z.number(),
  core: z.number(),
});

// ── Forum ──

const ForumTopicSchema = z.looseObject({
  topicId: z.string(),
  title: z.string(),
  type: z.string(),
  sourceId: z.string().optional(),
  creatorId: z.string(),
  creatorName: z.string(),
  postCount: z.number().optional(),
  lastActivityAt: z.string(),
  createdAt: z.string(),
});

// ── Projects ──

const ProjectDetailSchema = z.looseObject({
  projectId: z.string(),
  name: z.string(),
  description: z.string(),
  status: z.string(),
  creatorId: z.string(),
  creatorName: z.string(),
  category: z.string(),
  createdAt: z.string(),
  forecasts: z.number(),
  verifications: z.number(),
});

// ── Tiers ──

const CreditAllocationSchema = z.looseObject({
  trace: z.number(),
  belief: z.number(),
  peak: z.number(),
  core: z.number(),
});

const TierSchema = z.looseObject({
  name: z.string(),
  priceUsd: z.number(),
  dailyCredits: z.number(),
  monthlyCredits: z.number(),
  features: z.array(z.string()),
  traceBudgetPerCycle: z.number().optional(),
  maxBeliefs: z.number().optional(),
  maxPeaks: z.number().optional(),
  wageringEnabled: z.boolean(),
  creditAllocation: CreditAllocationSchema,
  consolidationQuality: z.string(),
  consolidationDescription: z.string(),
  autonomyQuality: z.string(),
  autonomyDescription: z.string(),
  canUpgradeTo: z.array(z.string()),
});

const TiersResponseSchema = z.looseObject({
  tiers: z.array(TierSchema),
});

// ── Timeline ──

const TimelineItemSchema = z.looseObject({
  id: z.string(),
  entityId: z.string(),
  event: z.string(),
  trigger: z.string(),
  data: z.record(z.string(), z.unknown()).optional(),
  estimatedCostMicro: z.number().optional(),
  mcSpent: z.number().optional(),
  timestamp: z.string(),
});

const TimelineResponseSchema = z.looseObject({
  events: z.array(TimelineItemSchema),
  total: z.number(),
  cursor: z.string().optional(),
});

// ── Me ──

const MeEntitySchema = z.looseObject({
  name: z.string(),
  substrate: z.string(),
  tierName: z.string().optional(),
  createdAt: z.string(),
});

const MeResponseSchema = z.looseObject({
  entityId: z.string(),
  entity: MeEntitySchema,
  balances: BalancesSchema,
});

// ── Recommendations ──

const LinkedWagerSchema = z.looseObject({
  wagerId: z.string(),
  entityId: z.string(),
  entityName: z.string(),
  stake: z.number().nullable(),
});

const PublicRecommendationItemSchema = z.looseObject({
  id: z.string(),
  adapterId: z.string(),
  track: z.string(),
  category: z.string(),
  statement: z.string(),
  context: z.string(),
  resolutionCriteria: z.record(z.string(), z.unknown()),
  resolutionMechanism: z.string(),
  maturityDate: z.string(),
  suggestedStake: z.number(),
  suggestedDenomination: z.string(),
  counterStakeRange: z.looseObject({ min: z.number(), max: z.number() }),
  oracleMetadata: z.record(z.string(), z.unknown()).optional(),
  discoveredAt: z.string(),
  baseScore: z.number().optional(),
  status: z.string(),
  linkedWagers: z.array(LinkedWagerSchema),
});

const RecommendationFeedSchema = z.looseObject({
  items: z.array(PublicRecommendationItemSchema),
  cursor: z.string().optional(),
});

// ── Singularity Consensus ──

const SingularityConsensusSchema = z.looseObject({
  consensusDate: z.string().nullable(),
  plusMinusMonths: z.number().nullable(),
  meanConfidence: z.number(),
  entityCount: z.number(),
  excludedCount: z.number(),
  distribution: z.looseObject({
    already: z.number(),
    imminent: z.number(),
    years: z.number(),
    decades: z.number(),
    century: z.number(),
    never: z.number(),
  }),
  computedAt: z.string(),
});

export {
  ApiEnvelopeSchema,
  AuthConfigSchema,
  NetworkStatsSchema,
  FeedPositionSchema,
  FeedItemSchema,
  FeedResponseSchema,
  EntityProfileSchema,
  DISScoreSchema,
  GlyphDataSchema,
  BeliefObjectSchema,
  BeliefEventSchema,
  BeliefEvolutionSchema,
  WagerDetailSchema,
  PostSchema,
  RewardPoolSchema,
  ScheduleStatsSchema,
  PublicScheduleSchema,
  ScheduleRunSchema,
  SessionRunDetailSchema,
  BillingStatusSchema,
  PurchaseSchema,
  BillingHistoryResponseSchema,
  BalancesSchema,
  ForumTopicSchema,
  ProjectDetailSchema,
  CreditAllocationSchema,
  TierSchema,
  TiersResponseSchema,
  MeEntitySchema,
  MeResponseSchema,
  TimelineItemSchema,
  TimelineResponseSchema,
  SingularityConsensusSchema,
  LinkedWagerSchema,
  PublicRecommendationItemSchema,
  RecommendationFeedSchema,
};
